import React, { useCallback } from 'react';
import { cn } from '@/lib/utils';
import Input from '../../ui/Input';
import { formatCurrency } from '../../../utils/formatters';
import type { CellLayoutProps } from './types';
import { useInlineEdit } from './useInlineEdit';

const cellInputClass =
    'h-7 min-h-0 px-0 py-0 shadow-none rounded-none border-0 border-b border-primary bg-transparent focus-visible:ring-0 focus-visible:border-primary';

export function CellLayout({ cellKind, value, onChange }: Omit<CellLayoutProps, 'layout'>) {
    const isPrice = cellKind === 'price';

    const toDisplayString = useCallback((v: number) => v.toString(), []);

    const parse = useCallback(
        (raw: string): number | null => {
            if (isPrice) {
                const num = parseFloat(raw);
                return Number.isNaN(num) ? null : num;
            }
            const num = parseInt(raw, 10);
            return Number.isNaN(num) || num <= 0 ? null : num;
        },
        [isPrice],
    );

    const onCommit = useCallback(
        (parsed: number) => {
            onChange(parsed);
        },
        [onChange],
    );

    const edit = useInlineEdit({
        value,
        toDisplayString,
        parse,
        onCommit,
        selectOnFocus: true,
    });

    const displayText = isPrice ? formatCurrency(value) : `${value}×`;
    const ariaLabel = isPrice
        ? `Preço: ${formatCurrency(value)}. Clique para editar`
        : `Quantidade: ${value}. Clique para editar`;

    if (edit.isEditing) {
        return (
            <div
                className={cn('inline-flex', isPrice ? 'w-24' : 'w-9 min-w-[2rem]')}
                onClick={edit.stopPropagation}
            >
                <Input
                    ref={edit.inputRef}
                    id={isPrice ? 'product-edit-price' : undefined}
                    name={isPrice ? 'product-edit-price' : undefined}
                    type="number"
                    step={isPrice ? '0.01' : undefined}
                    min={isPrice ? undefined : 1}
                    autoComplete="off"
                    aria-label={isPrice ? 'Editar preço' : 'Editar quantidade'}
                    value={edit.localValue}
                    onChange={(e) => edit.setLocalValue(e.target.value)}
                    onBlur={edit.save}
                    onKeyDown={edit.handleKeyDown}
                    inputSize="sm"
                    containerClassName="mb-0 gap-0 w-full"
                    className={cn(cellInputClass, isPrice ? 'text-right' : 'text-center')}
                />
            </div>
        );
    }

    return (
        <span
            role="button"
            tabIndex={0}
            aria-label={ariaLabel}
            onClick={(e) => {
                edit.stopPropagation(e);
                edit.startEditing();
            }}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    edit.startEditing();
                }
            }}
            className={
                isPrice
                    ? 'text-sm text-default cursor-pointer hover:bg-surface-subtle px-1.5 py-0.5 rounded-md transition-colors whitespace-nowrap'
                    : 'text-sm text-secondary font-semibold cursor-pointer hover:bg-surface-subtle px-1.5 py-0.5 rounded-md transition-colors min-w-[2rem] text-center'
            }
        >
            {displayText}
        </span>
    );
}
